import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Twitter, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import ktcLogo from '../assets/ktc.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    layanan: [
      { name: 'Paket Rumah', href: '#paket' },
      { name: 'Paket Bisnis', href: '#paket' },
      { name: 'Enterprise', href: '#kontak' },
      { name: 'Dedicated Line', href: '#kontak' },
    ],
    perusahaan: [
      { name: 'Tentang Kami', href: '#tentang' },
      { name: 'Karir', href: '#' },
      { name: 'Berita', href: '/berita', isLink: true },
      { name: 'Partner', href: '#' },
    ],
    bantuan: [
      { name: 'FAQ', href: '#' },
      { name: 'Cek Ketersediaan', href: '#kontak' },
      { name: 'Syarat & Ketentuan', href: '#' },
      { name: 'Kebijakan Privasi', href: '#' },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-[#0A0A0A] pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top Section */}
        <div className="grid lg:grid-cols-5 gap-12 pb-12 border-b border-white/10">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a 
              href="#beranda" 
              onClick={(e) => { e.preventDefault(); scrollToSection('#beranda'); }}
              className="flex items-center gap-3 mb-6"
            >
              <img 
                src={ktcLogo} 
                alt="KTC Logo" 
                className="h-12 w-auto"
              />
            </a>
            <p className="text-gray-400 mb-6 leading-relaxed">
              <strong className="text-white">Kedai Teknologi Celebes</strong> - Penyedia layanan internet 
              terpercaya di Sulawesi dengan koneksi fiber optik berkecepatan tinggi dan dukungan teknis 24/7.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-400">
                <MapPin className="w-4 h-4 text-brand-blue flex-shrink-0" />
                <span className="text-sm">Jl. Poros Polewali - Majene, Matakali, Polewali Mandar</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Phone className="w-4 h-4 text-brand-red flex-shrink-0" />
                <span className="text-sm">+62 8131-22222-30 (WhatsApp)</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Mail className="w-4 h-4 text-brand-blue flex-shrink-0" />
                <span className="text-sm">info@ktconline.id</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={index}
                    href={social.href}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-white/10 flex items-center justify-center text-gray-400 hover:text-brand-blue hover:border-brand-blue/30 transition-colors"
                    aria-label={social.label}
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Layanan</h4>
            <ul className="space-y-3">
              {footerLinks.layanan.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="footer-link text-sm hover:text-brand-blue transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Perusahaan</h4>
            <ul className="space-y-3">
              {footerLinks.perusahaan.map((link, index) => (
                <li key={index}>
                  {link.isLink ? (
                    <Link
                      to={link.href}
                      className="footer-link text-sm hover:text-brand-blue transition-colors"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="footer-link text-sm hover:text-brand-blue transition-colors"
                    >
                      {link.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Bantuan</h4>
            <ul className="space-y-3">
              {footerLinks.bantuan.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="footer-link text-sm hover:text-brand-blue transition-colors"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            &copy; {currentYear} Kedai Teknologi Celebes. Seluruh hak cipta dilindungi.
          </p>
          <div className="flex gap-6">
            <button className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
              Syarat & Ketentuan
            </button>
            <button className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
              Kebijakan Privasi
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

